/** Small hooks/components shared by the HUD pieces. */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ButtonName } from '@hf/shared';
import { useServices } from '../actions';
import { Key } from '../components';
import { ui } from '@hf/client-core/ui-model/store';
import type { HudPulse } from '@hf/client-core/ui-model/types';
export { promptDevice } from '@hf/client-core/ui-model/hud';

/**
 * Most recent pulse of a kind, cleared after `ms` (re-triggers on every new pulse id). A pulse that was already in
 * the store when the component mounted is history, not news: the HUD remounts for every match / lesson, and
 * replaying the last stored pulse flashed the previous play's GO! GO! GO! over the next match's first line-up
 * (QA r4: the newcomer's first real play said GO! while the hint said "STAY BEHIND THE LINE UNTIL GO!").
 */
export const usePulse = (kind: HudPulse['kind'], ms: number, player?: number): HudPulse | null => {
  const p = ui.pulse.value;
  const [live, setLive] = useState<HudPulse | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stale = useRef<number | null>(p?.id ?? null);
  useEffect(() => {
    if (!p || p.id === stale.current) return;
    stale.current = null;
    if (p.kind !== kind || (player !== undefined && p.player !== player)) return;
    setLive(p);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      setLive((cur) => (cur?.id === p.id ? null : cur));
    }, ms);
  }, [p?.id]);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return live;
};

/** Keycap for a logical button on a device ("J", "A", "SPACE"). */
export function ButtonCap({ device, button }: { device: string | null; button: ButtonName }) {
  const { input } = useServices();
  if (!device) return null;
  let label = '—';
  try {
    label = input.promptFor(device, button);
  } catch {
    /* unknown device */
  }
  return <Key label={label} pad={device.startsWith('pad:')} />;
}
